import React, { useState } from 'react';
import { 
  extractYouTubeVideoId, 
  createYouTubeVideoObject, 
  validateYouTubeUrl,
  generateYouTubeThumbnail 
} from '../../utils/youtubeUtils';
import './FileUpload.css';

const YouTubeVideoInput = ({ onVideoAdd, label = "Ajouter une vidéo YouTube" }) => {
  const [url, setUrl] = useState('');
  const [title, setTitle] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');

  const handleUrlChange = (e) => {
    const newUrl = e.target.value;
    setUrl(newUrl);
    setError('');
    
    // Auto-preview when valid URL is entered
    if (validateYouTubeUrl(newUrl)) {
      const videoId = extractYouTubeVideoId(newUrl);
      setPreview({
        videoId,
        thumbnail: generateYouTubeThumbnail(videoId),
        url: newUrl
      });
      
      // Auto-fill title if empty
      if (!title) {
        setTitle(`Vidéo YouTube ${videoId}`);
      }
    } else {
      setPreview(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!url.trim()) {
      setError('Veuillez saisir une URL YouTube');
      return;
    }
    
    if (!validateYouTubeUrl(url)) {
      setError('URL YouTube invalide. Formats acceptés: youtube.com/watch?v=..., youtu.be/..., etc.');
      return;
    }
    
    if (!title.trim()) {
      setError('Veuillez saisir un titre pour la vidéo');
      return;
    }
    
    setIsProcessing(true);
    setError('');
    
    try {
      const videoObject = createYouTubeVideoObject(url, title.trim());
      
      // Call the callback with the new video object
      onVideoAdd(videoObject);
      
      // Reset form
      setUrl('');
      setTitle('');
      setPreview(null);
      
      console.log('✅ YouTube video added successfully:', videoObject);
      
    } catch (error) {
      console.error('❌ Error adding YouTube video:', error);
      setError(error.message || 'Erreur lors de l\'ajout de la vidéo YouTube');
    } finally {
      setIsProcessing(false);
    }
  };

  const clearForm = () => {
    setUrl('');
    setTitle('');
    setPreview(null);
    setError('');
  };

  return (
    <div className="youtube-video-input">
      <div className="file-upload-section">
        <div className="upload-header">
          <h4>
            <i className="fab fa-youtube" style={{ color: '#ff0000' }}></i>
            {label}
          </h4>
        </div>
        
        <form onSubmit={handleSubmit} className="youtube-form">
          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-link"></i>
              URL YouTube
            </label>
            <input
              type="url"
              value={url}
              onChange={handleUrlChange}
              placeholder="https://www.youtube.com/watch?v=... ou https://youtu.be/..."
              className="youtube-url-input"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: error && !validateYouTubeUrl(url) ? '2px solid #ef4444' : '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.9rem'
              }}
            />
            {preview && (
              <div className="url-valid-indicator" style={{
                marginTop: '5px',
                fontSize: '0.8rem',
                color: '#10b981',
                display: 'flex',
                alignItems: 'center',
                gap: '5px'
              }}>
                <i className="fas fa-check-circle"></i>
                URL YouTube valide détectée
              </div>
            )}
          </div>
          
          <div className="form-group">
            <label className="form-label">
              <i className="fas fa-heading"></i>
              Titre de la vidéo
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Titre de votre vidéo"
              className="youtube-title-input"
              style={{
                width: '100%',
                padding: '10px 12px',
                border: error && !title.trim() ? '2px solid #ef4444' : '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.9rem'
              }}
            />
          </div>
          
          {preview && (
            <div className="youtube-preview" style={{
              marginTop: '15px',
              padding: '15px',
              border: '1px solid #e5e7eb',
              borderRadius: '8px',
              backgroundColor: '#f9fafb'
            }}>
              <h5 style={{ margin: '0 0 10px 0', color: '#374151' }}>
                <i className="fas fa-eye"></i>
                Aperçu
              </h5>
              <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-start' }}>
                <div style={{ flexShrink: 0 }}>
                  <img 
                    src={preview.thumbnail} 
                    alt="Thumbnail"
                    style={{
                      width: '120px',
                      height: '90px',
                      objectFit: 'cover',
                      borderRadius: '4px',
                      border: '1px solid #d1d5db'
                    }}
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTIwIiBoZWlnaHQ9IjkwIiB2aWV3Qm94PSIwIDAgMTIwIDkwIiBmaWxsPSJub25lIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPgo8cmVjdCB3aWR0aD0iMTIwIiBoZWlnaHQ9IjkwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik00OCA0MEw3MiA1NUw0OCA3MFY0MFoiIGZpbGw9IiM2QjcyODAiLz4KPC9zdmc+';
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ margin: '0 0 5px 0', fontWeight: '500', color: '#1f2937' }}>
                    {title || 'Titre de la vidéo'}
                  </p>
                  <p style={{ margin: '0', fontSize: '0.8rem', color: '#6b7280' }}>
                    ID: {preview.videoId}
                  </p>
                  <p style={{ margin: '5px 0 0 0', fontSize: '0.8rem', color: '#6b7280' }}>
                    URL: {preview.url}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {error && (
            <div className="error-message" style={{
              marginTop: '10px',
              padding: '10px',
              backgroundColor: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '6px',
              color: '#dc2626',
              fontSize: '0.9rem',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <i className="fas fa-exclamation-triangle"></i>
              {error}
            </div>
          )}
          
          <div className="form-actions" style={{
            marginTop: '20px',
            display: 'flex',
            gap: '10px',
            justifyContent: 'flex-end'
          }}>
            <button
              type="button"
              onClick={clearForm}
              className="btn-secondary"
              style={{
                padding: '10px 15px',
                background: '#6b7280',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '0.9rem'
              }}
            >
              <i className="fas fa-times"></i>
              Annuler
            </button>
            <button
              type="submit"
              disabled={isProcessing || !url || !title.trim() || !validateYouTubeUrl(url)}
              className="btn-primary"
              style={{
                padding: '10px 15px',
                background: isProcessing || !url || !title.trim() || !validateYouTubeUrl(url) ? '#9ca3af' : '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: isProcessing || !url || !title.trim() || !validateYouTubeUrl(url) ? 'not-allowed' : 'pointer',
                fontSize: '0.9rem',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {isProcessing ? (
                <>
                  <i className="fas fa-spinner fa-spin"></i>
                  Ajout...
                </>
              ) : (
                <>
                  <i className="fas fa-plus"></i>
                  Ajouter la vidéo
                </>
              )}
            </button>
          </div>
        </form>
        
        <div className="youtube-help" style={{
          marginTop: '15px',
          padding: '10px',
          backgroundColor: '#f0f9ff',
          border: '1px solid #bfdbfe',
          borderRadius: '6px',
          fontSize: '0.8rem',
          color: '#1e40af'
        }}>
          <div style={{ fontWeight: '500', marginBottom: '5px' }}>
            <i className="fas fa-info-circle"></i>
            Formats d'URL supportés:
          </div>
          <ul style={{ margin: '0', paddingLeft: '20px' }}>
            <li>https://www.youtube.com/watch?v=VIDEO_ID</li>
            <li>https://youtu.be/VIDEO_ID</li>
            <li>https://www.youtube.com/embed/VIDEO_ID</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default YouTubeVideoInput;
